import type { NxHue } from '../lib/hue';

export type NxResourceGridKey = string | number;

/** One row: usually a person. */
export interface NxResourceGridRow {
  id: NxResourceGridKey;
  label: string;
  /** Muted second line (department, weekly hours, …). */
  sublabel?: string;
}

/** One column: a day, a week — whatever unit the caller plans in. */
export interface NxResourceGridColumn {
  key: string;
  label: string;
  /** Muted second line (weekday, calendar week, …). */
  sublabel?: string;
  /** Non-working for everybody (weekend, public holiday): the whole column is hatched. */
  nonWorking?: boolean;
  /** Highlights the column (today, current week). */
  current?: boolean;
}

/**
 * Content of one cell. `load` is the booked share of the capacity (1 = full);
 * it picks the cell's colour step. `text` is shown in the cell, `title` is its
 * tooltip and accessible description.
 *
 * `text` is meant for rows without blocks (the heatmap): blocks lie over the
 * cells and would cover it. In a deployment plan put the figure in `title`.
 */
export interface NxResourceGridCell {
  load?: number | null;
  text?: string;
  title?: string;
  /** Non-working for this row only (vacation, part-time day). Hatched like a non-working column. */
  nonWorking?: boolean;
}

export type NxResourceGridTone = 'brand' | 'info' | 'success' | 'warning' | 'danger' | 'neutral';

/**
 * A figure in the summary column (per row) or the footer row (per column):
 * the week's hours of a person, the support staffing of a day. `tone` marks it
 * (e.g. `danger` when a day is understaffed).
 */
export interface NxResourceGridFigure {
  text: string;
  title?: string;
  tone?: 'neutral' | 'success' | 'warning' | 'danger';
}

export function toFigure(
  value: NxResourceGridFigure | string | null | undefined
): NxResourceGridFigure | null {
  if (value == null || value === '') return null;
  return typeof value === 'string' ? { text: value } : value;
}

/** A bar spanning `span` columns from `start` (a column key) in one row. */
export interface NxResourceGridBlock {
  id: NxResourceGridKey;
  rowId: NxResourceGridKey;
  start: string;
  /** Number of columns, at least 1. */
  span: number;
  label: string;
  /** Intent tone, or a category hue for bookings that are only grouped by kind. */
  tone?: NxResourceGridTone | NxHue;
  /** A locked block cannot be moved (by pointer or keyboard). */
  locked?: boolean;
}

/** A requested block move. */
export interface NxResourceGridBlockMove {
  block: NxResourceGridBlock;
  rowId: NxResourceGridKey;
  start: string;
}

/**
 * Colour step of a cell:
 * 0 = nothing booked, 1 = light (< 50 %), 2 = medium (< 85 %),
 * 3 = full (≤ 100 %), `over` = more than the capacity.
 */
export type NxResourceLoadLevel = 0 | 1 | 2 | 3 | 'over';

export function loadLevel(load: number | null | undefined): NxResourceLoadLevel | null {
  if (load == null || Number.isNaN(load)) return null;
  if (load <= 0) return 0;
  if (load < 0.5) return 1;
  if (load < 0.85) return 2;
  if (load <= 1) return 3;
  return 'over';
}

export interface PlacedBlock {
  block: NxResourceGridBlock;
  /** 0-based index of the start column. */
  column: number;
  /** Columns actually visible (clipped at the right edge). */
  span: number;
  /** Stacking lane inside the row, 0-based. */
  lane: number;
}

/**
 * Place a row's blocks on the visible columns and give overlapping blocks
 * separate lanes (greedy, in start order), so two bookings on the same day sit
 * one above the other instead of on top of each other. Blocks whose start
 * column is not visible are dropped.
 */
export function placeBlocks(
  blocks: NxResourceGridBlock[],
  columnIndex: Map<string, number>,
  columnCount: number
): { placed: PlacedBlock[]; lanes: number } {
  const positioned = blocks
    .map((block) => ({ block, column: columnIndex.get(block.start) ?? -1 }))
    .filter((b) => b.column >= 0)
    .sort((a, b) => a.column - b.column);

  /** Per lane: first free column. */
  const laneEnds: number[] = [];
  const placed: PlacedBlock[] = positioned.map(({ block, column }) => {
    const span = Math.max(1, Math.min(block.span, columnCount - column));
    let lane = laneEnds.findIndex((end) => end <= column);
    if (lane < 0) lane = laneEnds.length;
    laneEnds[lane] = column + span;
    return { block, column, span, lane };
  });
  return { placed, lanes: Math.max(1, laneEnds.length) };
}
